/**
 * Pure calculation functions for billing-rate hooks.
 * Extracted so tests can import them without pulling in the Supabase client.
 * The hooks in src/hooks/use-billing-rate*.ts wrap these with data fetching.
 */
import {
  calcAbsenceHours,
  calcMonthlyCapacity,
  isTaskLine,
  WORK_HOURS_PER_DAY,
} from "./billing-calculations";
import { osloMonthKey } from "./oslo-time";
import { DEFAULT_MONTHLY_CAPACITY_HOURS } from "./constants";
import { resolveBillingAttributionDate } from "./billing-attribution";

const COMPANY_TARGET_RATE = 65;

// ── Types ────────────────────────────────────────────────────

export interface MonthlyBillingData {
  month: number; // 1-12
  year: number;
  loggedHours: number;
  billableHours: number;
  capacity: number;
  absenceHours: number;
  rate: number | null;
  confirmed: boolean;
  confirmedAt: string | null;
}

export interface EmployeeBillingData {
  userId: string;
  fullName: string;
  avatarUrl: string | null;
  targetRate: number;
  ytdRate: number;
  /** Live hittil i år, inkludert inneværende måned med pro-ratert kapasitet. */
  liveYtdRate: number;
  monthlyCapacity: number;
  months: MonthlyBillingData[];
  requiredRate: number;
}

export interface BillingRatesData {
  employees: EmployeeBillingData[];
  teamYtdRate: number;
  /** Teamets live-rate, separat fra det avstemte bonusgrunnlaget. */
  teamLiveYtdRate: number;
  teamTargetRate: number;
  pendingConfirmations: Array<{
    userId: string;
    fullName: string;
    avatarUrl: string | null;
    month: number;
    year: number;
  }>;
}

export interface BillingRatesInput {
  users: Array<{
    id: string;
    full_name: string;
    avatar_url: string | null;
    monthly_capacity_hours: number | null;
    target_billing_rate: number | null;
  }>;
  billableAssignments: Array<{
    user_id: string;
    hours: number | null;
    task_status?: string;
    budget_line_id: string;
    completed_at: string | null;
    approved_at?: string | null;
    budget_lines: {
      project_id: string;
      cost_type: string | null;
      quantity_hours: number | null;
      // VIB-994: T&M-mersalg-linjer ekskluderes fra assignment-pathen når flagget er på.
      upsells?: { billing_mode: string | null } | null;
      projects?: {
        deleted_at: string | null;
        project_status: string | null;
        project_type?: string | null;
      } | null;
    } | null;
  }>;
  timeEntries: Array<{
    user_id: string;
    duration_seconds: number | null;
    start_date: string;
    budget_line_id: string | null;
  }>;
  absences: Array<{
    user_id: string;
    start_date: string;
    end_date: string;
    status?: string | null;
    // Eksplisitt timeoverstyring (deltimers-fravær som avspasering); når satt
    // tilskrives beløpet startDate-måneden i stedet for ukedager × 7,5t.
    hours?: number | null;
  }>;
  confirmations: Array<{
    user_id: string;
    month: number;
    confirmed_at: string;
  }>;
  // VIB-909: loggede time_entries på reisetid-linjer (cost_type='internal',
  // quantity_hours=0). Krediteres 1:1, attribuert til start_date-måneden.
  reisetidEntries?: ReisetidEntry[];
  // VIB-923: loggede time_entries på T&M-prosjekter (project_type='hourly').
  // T&M har ingen tilbuds-allokering — billable = førte timer. Krediteres 1:1,
  // attribuert til start_date-måneden, løpende uten godkjennings-gate (Erik-lock
  // 2026-06-07). Samme additive mønster som reisetid.
  hourlyEntries?: ReisetidEntry[];
  // VIB-994: loggede time_entries på T&M-mersalg-linjer (upsell.billing_mode='tm',
  // approved). Krediteres 1:1 når `tmEnabled` (feature_tm_upsell) er på — samme
  // additive mønster som reisetid/hourly.
  tmUpsellEntries?: ReisetidEntry[];
  /** VIB-994: speiler feature_tm_upsell. Av (default) → T&M-grenen er no-op. */
  tmEnabled?: boolean;
  // VIB-916: helligdager (ISO yyyy-mm-dd). Reduserer kapasitet på ukedager
  // (Man-Fre) × WORK_HOURS_PER_DAY, samme modell som fravær. Default tom.
  holidays?: string[];
  /** VIB-1622: siste måned (1-12) som inngår i avstemt hittil-i-år-rate. */
  reconciledThroughMonth?: number;
  year: number;
  now: Date;
}

// ── Delt akkumulator (VIB-896) ───────────────────────────────
// Én kanonisk kilde for "fakturerbare timer per (bruker, måned)". Brukes av
// calcBillingRatesData, calcCompletedTasksBilling og calcTeamCompletedTasksBilling
// så rate og grad alltid er samme tall. Regler er Erik-lock 2026-05-15/06-02:
// - kun isTaskLine (cost_type='internal' AND quantity_hours>0); null budget_lines
//   tolereres (legacy/tester) og inkluderes.
// - ekskluder soft-deletede og kansellerte prosjekter.
// - periodedato følger overgangsregelen i billing-attribution.ts.

export interface BillableAccumRow {
  user_id?: string;
  hours: number | null;
  task_status?: string;
  budget_line_id: string;
  completed_at?: string | null;
  approved_at?: string | null;
  budget_lines?: {
    cost_type: string | null;
    quantity_hours: number | null;
    // VIB-994: mersalgets faktureringsmodus. T&M-mersalg-linjer ekskluderes fra
    // assignment-pathen (teller 1:1 via tmUpsellEntries i stedet) når flagget er på.
    upsells?: { billing_mode: string | null } | null;
    projects?: {
      deleted_at: string | null;
      project_status: string | null;
      project_type?: string | null;
    } | null;
  } | null;
}

// Rader med budget_line_id === null skippes i akkumulatoren (ingen fallback-nøkkel).
export interface AttributionEntry {
  user_id?: string;
  budget_line_id: string | null;
  start_date: string;
}

export interface UserMonthBillable {
  hours: number;
  count: number;
}

// VIB-909: Reisetid (medgått) krediteres 1:1 fra LOGGEDE time_entries, ikke fra
// tildelte timer. En reisetid-linje har cost_type='internal' men quantity_hours=0,
// så den faller utenfor isTaskLine-pathen. Dette er en ADDITIV andre kilde.
// Måned attribueres fra time_entry.start_date.
export interface ReisetidEntry {
  user_id?: string;
  duration_seconds: number | null;
  start_date: string;
}

/**
 * key = `${user_id}-${month}` der month er 1-12.
 *
 * VIB-994: `tmEnabled` speiler feature_tm_upsell-flagget i RPC-en. Når på,
 * ekskluderes assignments på T&M-mersalg-linjer (billing_mode='tm') fra
 * tildelte-timer-pathen, og `tmUpsellEntries` (loggede timer på de samme
 * linjene) legges til 1:1 — samme mønster som reisetid/hourly. Når av
 * (default) er begge no-op, så oppførselen er identisk med før.
 */
export function accumulateBillableByUserMonth(
  assignments: BillableAccumRow[],
  timeEntries: AttributionEntry[],
  year: number,
  asOf: Date,
  reisetidEntries: ReisetidEntry[] = [],
  hourlyEntries: ReisetidEntry[] = [],
  tmUpsellEntries: ReisetidEntry[] = [],
  tmEnabled: boolean = false,
): Map<string, UserMonthBillable> {
  // Årsbinding (matcher last_entry-CTE i RPC-en): bare time_entries innenfor
  // `year` brukes til legacy-fallback-attribusjon. Hooks/breakdown henter
  // allerede årsfiltrerte entries, men eksplisitt filter her holder JS-orakelet
  // i lås med SQL også når kalt med fler-års-data (legacy completed_at=null).
  const lastEntryMap = new Map<string, string>();
  for (const te of timeEntries) {
    if (!te.budget_line_id) continue;
    if (osloMonthKey(te.start_date).year !== year) continue;
    const key = `${te.user_id ?? ""}-${te.budget_line_id}`;
    const existing = lastEntryMap.get(key);
    if (!existing || te.start_date > existing) lastEntryMap.set(key, te.start_date);
  }

  const acc = new Map<string, UserMonthBillable>();
  for (const a of assignments) {
    const bl = a.budget_lines;
    if (bl && !isTaskLine(bl)) continue;
    if (bl?.projects?.deleted_at != null) continue;
    if (bl?.projects?.project_status === "cancelled") continue;
    // VIB-923: T&M-prosjekter (hourly) bruker IKKE tildelte tilbuds-timer.
    // Deres billable kommer fra loggede hourlyEntries i stedet (additivt under).
    // Defensivt skip: i dag har hourly-prosjekter ingen task-line-assignments,
    // men dette hindrer dobbelttelling hvis det noensinne skjer.
    if (bl?.projects?.project_type === "hourly") continue;
    // VIB-994: T&M-mersalg-linjer teller 1:1 via tmUpsellEntries, ikke tildelte
    // timer — ekskluder dem her når flagget er på (unngår dobbelttelling).
    if (tmEnabled && bl?.upsells?.billing_mode === "tm") continue;

    const attributionDate = resolveBillingAttributionDate(
      a,
      lastEntryMap.get(`${a.user_id ?? ""}-${a.budget_line_id}`) ?? null,
      asOf,
    );
    if (!attributionDate) continue;
    const { year: attrYear, month } = osloMonthKey(attributionDate);
    if (attrYear !== year) continue;

    const key = `${a.user_id ?? ""}-${month}`;
    const prev = acc.get(key) ?? { hours: 0, count: 0 };
    prev.hours += Number(a.hours ?? 0);
    prev.count += 1;
    acc.set(key, prev);
  }

  // VIB-909 (reisetid) + VIB-923 (T&M-prosjekter) + VIB-994 (T&M-mersalg):
  // additive kilder — loggede timer 1:1, attribuert til start_date-måneden.
  // Alle er ført-time-baserte (ikke tildelte timer), så de behandles likt her.
  // T&M-mersalg tas kun med når flagget er på.
  const additiveEntries = tmEnabled
    ? [...reisetidEntries, ...hourlyEntries, ...tmUpsellEntries]
    : [...reisetidEntries, ...hourlyEntries];
  for (const te of additiveEntries) {
    const { year: teYear, month } = osloMonthKey(te.start_date);
    if (teYear !== year) continue;
    const key = `${te.user_id ?? ""}-${month}`;
    const prev = acc.get(key) ?? { hours: 0, count: 0 };
    prev.hours += Number(te.duration_seconds ?? 0) / 3600;
    prev.count += 1;
    acc.set(key, prev);
  }
  return acc;
}

/** Kollaps per-(bruker,måned)-akkumulator til per-måned (1-12), summert over brukere. */
export function collapseToMonths(
  acc: Map<string, UserMonthBillable>,
): Map<number, UserMonthBillable> {
  const out = new Map<number, UserMonthBillable>();
  for (const [key, val] of acc) {
    const month = Number(key.slice(key.lastIndexOf("-") + 1));
    const prev = out.get(month) ?? { hours: 0, count: 0 };
    prev.hours += val.hours;
    prev.count += val.count;
    out.set(month, prev);
  }
  return out;
}

/** Kanonisk faktureringsgrad: 1 desimal. null hvis ingen kapasitet. */
export function calcBillingRate(billableHours: number, capacity: number): number | null {
  return capacity > 0 ? Math.round((billableHours / capacity) * 1000) / 10 : null;
}

/**
 * Andel av inneværende måned som er gjennomført, målt i ukedager (Man-Fre):
 * arbeidsdager t.o.m. `now` / totalt antall arbeidsdager i måneden. Returnerer
 * 0..1. Brukes til å pro-rate månedskapasiteten når inneværende (uavsluttede)
 * måned skal inngå i en justert faktureringsgrad, slik at teller (fullførte
 * timer hittil) og nevner (kapasitet hittil) dekker samme tidsrom.
 */
export function calcMonthElapsedWeekdayFraction(now: Date): number {
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indeksert
  const today = now.getDate();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  let elapsed = 0;
  let total = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const dow = new Date(year, month, d).getDay(); // 0=søn, 6=lør (lokal)
    if (dow === 0 || dow === 6) continue;
    total += 1;
    if (d <= today) elapsed += 1;
  }
  return total > 0 ? elapsed / total : 0;
}

/**
 * Faktureringsgrad justert for inneværende måned: YTD (lukkede måneder) PLUSS
 * inneværende måned, der inneværende måneds kapasitet pro-rates til arbeidsdager
 * passert (`monthFraction`). Fakturerte timer hittil i måneden tas med fullt ut.
 *
 * Display-only indikator ved siden av den autoritative `teamYtdRate` — den
 * Erik-låste YTD-formelen (lukkede måneder) er fortsatt bonus-kriteriet. Leser
 * de samme per-(bruker, måned) billable/capacity-tallene som `teamYtdRate` er
 * montert fra, så de to kan ikke divergere på det overlappende grunnlaget.
 * Returnerer null hvis ingen justert kapasitet (unngår divisjon på 0).
 */
export function calcCurrentMonthAdjustedTeamRate(
  employees: EmployeeBillingData[],
  currentMonth: number,
  monthFraction: number,
): number | null {
  let billable = 0;
  let capacity = 0;
  for (const emp of employees) {
    for (const mo of emp.months) {
      if (mo.month < currentMonth) {
        billable += mo.billableHours;
        capacity += mo.capacity;
      } else if (mo.month === currentMonth) {
        billable += mo.billableHours;
        capacity += mo.capacity * monthFraction;
      }
    }
  }
  return calcBillingRate(billable, capacity);
}

/**
 * Teamets fakturerbare timer og kapasitet per måned til og med `throughMonth`,
 * summert over de samme ansatt-månedene som `teamYtdRate` (VIB-2990).
 */
export function teamBillingByMonth(
  employees: EmployeeBillingData[],
  throughMonth: number,
): Array<{ month: number; billableHours: number; capacity: number; rate: number | null }> {
  const rows = [];
  for (let month = 1; month <= throughMonth; month++) {
    let billableHours = 0;
    let capacity = 0;
    for (const emp of employees) {
      const m = emp.months.find((x) => x.month === month);
      if (m) {
        billableHours += m.billableHours;
        capacity += m.capacity;
      }
    }
    rows.push({ month, billableHours, capacity, rate: calcBillingRate(billableHours, capacity) });
  }
  return rows;
}

/**
 * Live faktureringsgrad for én serie månedsdata. Tidligere måneder tas med i
 * sin helhet; inneværende måneds kapasitet avgrenses til passerte ukedager.
 * Dette er en visningsverdi og endrer aldri den avstemte `ytdRate`-fasiten.
 */
export function calcLiveBillingRate(
  months: MonthlyBillingData[],
  currentMonth: number,
  monthFraction: number,
): number | null {
  let billable = 0;
  let capacity = 0;
  for (const month of months) {
    if (month.month < currentMonth) {
      billable += month.billableHours;
      capacity += month.capacity;
    } else if (month.month === currentMonth) {
      billable += month.billableHours;
      capacity += month.capacity * monthFraction;
    }
  }
  return calcBillingRate(billable, capacity);
}

/** Per-måned (1-12) helligdags-timer: helligdager på ukedag (Man-Fre) × WORK_HOURS_PER_DAY. */
export function calcHolidayHoursByMonth(holidays: string[], year: number): Map<number, number> {
  const map = new Map<number, number>();
  for (const h of holidays) {
    const [y, m, day] = h.split("-").map(Number);
    if (y !== year || !m || !day) continue;
    const dow = new Date(y, m - 1, day).getDay(); // 0=søn, 6=lør (lokal — unngår UTC-skifte)
    if (dow === 0 || dow === 6) continue;
    map.set(m, (map.get(m) ?? 0) + WORK_HOURS_PER_DAY);
  }
  return map;
}

export interface MonthBillingData {
  month: number;
  label: string;
  tasksCompleted: number;
  assignedHours: number;
  availableHours: number;
  absenceHours: number;
  grad: number;
}

const MONTH_NAMES = [
  "Januar", "Februar", "Mars", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Desember",
];

// ── useBillingRates ──────────────────────────────────────────

/**
 * Pure calculation of billing rates data from pre-fetched DB rows.
 *
 * Rules (Erik-lock 2026-05-15, se docs/kpi-source-of-truth.md § Faktureringsgrad):
 * - T.o.m. august 2026 teller completed/approved etter completed_at.
 * - Fra september 2026 teller kun approved; nye godkjenninger bruker approved_at.
 *   Historiske godkjenninger beholder completed_at-perioden.
 * - Kun `isTaskLine`-budget_lines teller (cost_type='internal' AND quantity_hours>0).
 *   Interne kostnader (utstyrsutleie etc.) telles IKKE.
 * - Månedsattribusjon: se resolveBillingAttributionDate. Nye godkjenninger
 *   bruker approved_at; siste time_entry er kun fallback for legacy-rader.
 * - Capacity = monthly_capacity_hours − absence hours (weekdays only, 7.5h/day,
 *   alle ikke-rejected fraværstyper).
 * - Rate kan overstige 100% (effektivitet belønnes).
 */
export function calcBillingRatesData(input: BillingRatesInput): BillingRatesData {
  const billableAcc = accumulateBillableByUserMonth(
    input.billableAssignments,
    input.timeEntries.map((te) => ({
      user_id: te.user_id, budget_line_id: te.budget_line_id, start_date: te.start_date,
    })),
    input.year,
    input.now,
    input.reisetidEntries ?? [],
    input.hourlyEntries ?? [],
    input.tmUpsellEntries ?? [],
    input.tmEnabled ?? false,
  );
  return assembleBillingRatesFromBillableAcc({
    users: input.users,
    billableAcc,
    timeEntries: input.timeEntries,
    absences: input.absences,
    confirmations: input.confirmations,
    holidays: input.holidays ?? [],
    year: input.year,
    now: input.now,
    reconciledThroughMonth: input.reconciledThroughMonth,
  });
}

/**
 * Monterer BillingRatesData fra en ferdig billable-akkumulator
 * (`Map<"${userId}-${month}", UserMonthBillable>`) + capacity-input.
 *
 * WP6 / TOPP-4: ekstrahert fra calcBillingRatesData så både JS-pathen
 * (accumulateBillableByUserMonth) og RPC-pathen (get_billing_rates) deler
 * NØYAKTIG én monteringsvei. De kan dermed ikke divergere på rate/grad.
 * calcBillingRatesData kaller denne med JS-aggregeringen; use-billing-rates
 * kaller den med RPC-resultatet konvertert til samme Map.
 */
export function assembleBillingRatesFromBillableAcc(args: {
  users: BillingRatesInput["users"];
  billableAcc: Map<string, UserMonthBillable>;
  timeEntries: BillingRatesInput["timeEntries"];
  absences: BillingRatesInput["absences"];
  confirmations: BillingRatesInput["confirmations"];
  holidays: string[];
  year: number;
  now: Date;
  reconciledThroughMonth?: number;
}): BillingRatesData {
  const { users, billableAcc, timeEntries, absences, confirmations, year, now } = args;
  const holidayByMonth = calcHolidayHoursByMonth(args.holidays, year);

  const confirmedMap = new Map<string, string>();
  for (const c of confirmations) {
    confirmedMap.set(`${c.user_id}-${c.month}`, c.confirmed_at);
  }

  const loggedMap = new Map<string, number>();
  for (const te of timeEntries) {
    // Oslo-attribuer loggede timer (samme som billableAcc), ellers kan en
    // time-entry nær midnatt havne i ulik måned for logget vs fakturert.
    const { year: entryYear, month } = osloMonthKey(te.start_date);
    // LOW-2: guard against time_entries from adjacent years leaking through the
    // fetch boundary (was .lte year+1-01-01, now .lt year+1-01-01, but keep
    // the calc-side guard as defence-in-depth).
    if (entryYear !== year) continue;
    const key = `${te.user_id}-${month}`;
    loggedMap.set(key, (loggedMap.get(key) ?? 0) + (te.duration_seconds ?? 0) / 3600);
  }

  const currentMonth = now.getFullYear() === year ? now.getMonth() + 1 : 12;
  const liveMonthFraction = now.getFullYear() === year
    ? calcMonthElapsedWeekdayFraction(now)
    : 1;
  const reconciledThroughMonth = Math.min(
    12,
    Math.max(0, args.reconciledThroughMonth ?? currentMonth - 1),
  );

  const employees: EmployeeBillingData[] = users.map((user) => {
    const monthlyCapacity = user.monthly_capacity_hours ?? DEFAULT_MONTHLY_CAPACITY_HOURS;
    const userAbsences = absences.filter((a) => a.user_id === user.id);
    const months: MonthlyBillingData[] = [];
    let ytdBillable = 0;
    let ytdCapacity = 0;

    for (let m = 1; m <= currentMonth; m++) {
      const billableHours = billableAcc.get(`${user.id}-${m}`)?.hours ?? 0;
      const loggedHours = loggedMap.get(`${user.id}-${m}`) ?? 0;
      const absenceHours = calcAbsenceHours(
        userAbsences.map((a) => ({ startDate: a.start_date, endDate: a.end_date, status: a.status ?? undefined, hours: a.hours })),
        m,
        year,
      );
      const capacity = calcMonthlyCapacity(monthlyCapacity, absenceHours + (holidayByMonth.get(m) ?? 0));
      const confirmed = confirmedMap.has(`${user.id}-${m}`);
      const confirmedAt = confirmedMap.get(`${user.id}-${m}`) ?? null;
      const rate = calcBillingRate(billableHours, capacity);

      months.push({
        month: m,
        year,
        loggedHours: Math.round(loggedHours * 10) / 10,
        billableHours: Math.round(billableHours * 10) / 10,
        capacity: Math.round(capacity * 10) / 10,
        absenceHours: Math.round(absenceHours * 10) / 10,
        rate,
        confirmed,
        confirmedAt,
      });

      if (m <= reconciledThroughMonth) {
        ytdBillable += billableHours;
        ytdCapacity += capacity;
      }
    }

    const targetRate = user.target_billing_rate ?? COMPANY_TARGET_RATE;
    const ytdRate = calcBillingRate(ytdBillable, ytdCapacity) ?? 0;
    const liveYtdRate = calcLiveBillingRate(
      months,
      currentMonth,
      liveMonthFraction,
    ) ?? 0;

    const completedMonths = reconciledThroughMonth;
    const remainingMonths = 12 - completedMonths;
    const totalYearCapacity = monthlyCapacity * 12;
    const neededBillable = (targetRate / 100) * totalYearCapacity - ytdBillable;
    const remainingCapacity = monthlyCapacity * remainingMonths;
    const requiredRate = remainingCapacity > 0
      ? Math.round((neededBillable / remainingCapacity) * 1000) / 10
      : 0;

    return {
      userId: user.id,
      fullName: user.full_name,
      avatarUrl: user.avatar_url,
      targetRate,
      ytdRate,
      liveYtdRate,
      monthlyCapacity,
      months,
      requiredRate,
    };
  });

  let totalTeamBillable = 0;
  let totalTeamCapacity = 0;
  for (const emp of employees) {
    for (const m of emp.months) {
      if (m.month <= reconciledThroughMonth) {
        totalTeamBillable += m.billableHours;
        totalTeamCapacity += m.capacity;
      }
    }
  }
  const teamYtdRate = calcBillingRate(totalTeamBillable, totalTeamCapacity) ?? 0;
  const teamLiveYtdRate = calcCurrentMonthAdjustedTeamRate(
    employees,
    currentMonth,
    liveMonthFraction,
  ) ?? 0;

  const pendingConfirmations: BillingRatesData["pendingConfirmations"] = [];
  const prevMonth = currentMonth - 1;
  if (prevMonth >= 1) {
    for (const emp of employees) {
      const monthData = emp.months.find((m) => m.month === prevMonth);
      if (monthData && !monthData.confirmed) {
        pendingConfirmations.push({
          userId: emp.userId,
          fullName: emp.fullName,
          avatarUrl: emp.avatarUrl,
          month: prevMonth,
          year,
        });
      }
    }
  }

  return {
    employees,
    teamYtdRate,
    teamLiveYtdRate,
    teamTargetRate: COMPANY_TARGET_RATE,
    pendingConfirmations,
  };
}

// ── useCompletedTasksBilling ─────────────────────────────────

export interface Assignment {
  hours: number | null;
  budget_line_id: string;
  user_id?: string;
  task_status?: string;
  completed_at?: string | null;
  approved_at?: string | null;
  budget_lines?: {
    cost_type: string | null;
    quantity_hours: number | null;
    projects?: {
      deleted_at: string | null;
      project_status: string | null;
    } | null;
  } | null;
}

interface TimeEntry {
  user_id?: string;
  budget_line_id: string | null;
  start_date: string;
}

interface Absence {
  start_date: string;
  end_date: string;
  // Eksplisitt timeoverstyring (deltimers-fravær som avspasering).
  hours?: number | null;
}

function applyAbsenceHours(
  months: MonthBillingData[],
  absences: Absence[],
  year: number,
): void {
  for (const a of absences) {
    if (a.hours != null) {
      const start = new Date(a.start_date);
      if (start.getFullYear() === year) {
        months[start.getMonth()].absenceHours += a.hours;
      }
      continue;
    }
    const start = new Date(a.start_date);
    const end = new Date(a.end_date);
    const cursor = new Date(start);
    while (cursor <= end) {
      if (cursor.getFullYear() === year) {
        const day = cursor.getDay();
        if (day !== 0 && day !== 6) {
          months[cursor.getMonth()].absenceHours += WORK_HOURS_PER_DAY;
        }
      }
      cursor.setDate(cursor.getDate() + 1);
    }
  }
}

function initMonths(totalCapacity: number): MonthBillingData[] {
  return Array.from({ length: 12 }, (_, i) => ({
    month: i,
    label: MONTH_NAMES[i],
    tasksCompleted: 0,
    assignedHours: 0,
    availableHours: totalCapacity,
    absenceHours: 0,
    grad: 0,
  }));
}

function finalizeMonths(
  months: MonthBillingData[],
  year: number,
  now: Date,
): MonthBillingData[] {
  const currentMonth = now.getFullYear() === year ? now.getMonth() : 11;
  return months.filter((m) => m.month <= currentMonth || m.tasksCompleted > 0);
}

/** Delt kjerne for per-måned-billing gitt en ferdig beregnet total kapasitet. */
function calcMonthlyBillingFromCapacity(input: {
  assignments: Assignment[];
  timeEntries: TimeEntry[];
  capacity: number;
  absences: Absence[];
  year: number;
  now: Date;
  reisetidEntries?: ReisetidEntry[];
  hourlyEntries?: ReisetidEntry[];
  holidays: string[];
}): MonthBillingData[] {
  const { assignments, timeEntries, capacity, absences, year, now, holidays } = input;
  const reisetidEntries = input.reisetidEntries ?? [];
  const hourlyEntries = input.hourlyEntries ?? [];
  const holidayByMonth = calcHolidayHoursByMonth(holidays, year);

  const months = initMonths(capacity);
  applyAbsenceHours(months, absences, year);
  // VIB-916: legg helligdags-timer til fravær per måned (1-indeksert kilde,
  // months[] er 0-indeksert) før availableHours beregnes.
  months.forEach((mo, i) => { mo.absenceHours += holidayByMonth.get(i + 1) ?? 0; });
  months.forEach((m) => {
    m.availableHours = Math.max(0, capacity - m.absenceHours);
  });

  const perMonth = collapseToMonths(
    accumulateBillableByUserMonth(
      assignments as BillableAccumRow[],
      timeEntries.map((te) => ({ user_id: te.user_id, budget_line_id: te.budget_line_id, start_date: te.start_date })),
      year,
      now,
      reisetidEntries,
      hourlyEntries,
    ),
  );
  for (const [month1to12, val] of perMonth) {
    const idx = month1to12 - 1;
    months[idx].tasksCompleted = val.count;
    months[idx].assignedHours = val.hours;
  }

  months.forEach((m) => {
    m.grad = calcBillingRate(m.assignedHours, m.availableHours) ?? 0;
  });

  return finalizeMonths(months, year, now);
}

export function calcCompletedTasksBilling(input: {
  assignments: Assignment[];
  timeEntries: TimeEntry[];
  capacity: number;
  absences: Absence[];
  year: number;
  now: Date;
  reisetidEntries?: ReisetidEntry[];
  hourlyEntries?: ReisetidEntry[];
  holidays?: string[];
}): MonthBillingData[] {
  return calcMonthlyBillingFromCapacity({ ...input, holidays: input.holidays ?? [] });
}

export function calcTeamCompletedTasksBilling(input: {
  assignments: Assignment[];
  timeEntries: TimeEntry[];
  users: Array<{ monthly_capacity_hours: number | null }>;
  absences: Absence[];
  year: number;
  now: Date;
  reisetidEntries?: ReisetidEntry[];
  hourlyEntries?: ReisetidEntry[];
  holidays?: string[];
}): MonthBillingData[] {
  const { assignments, timeEntries, users, absences, year, now, reisetidEntries, hourlyEntries, holidays } = input;
  const totalCapacity = users.reduce(
    (s, u) => s + Number(u.monthly_capacity_hours ?? DEFAULT_MONTHLY_CAPACITY_HOURS),
    0,
  );
  return calcMonthlyBillingFromCapacity({ assignments, timeEntries, capacity: totalCapacity, absences, year, now, reisetidEntries, hourlyEntries, holidays: holidays ?? [] });
}
